<div class="db-root">
  @if (user(); as u) {

    <!-- Header -->
    <div class="db-header">
      <div>
        <div class="db-eyebrow">YOUR LEAGUES · SEASON {{ currentYear }}</div>
        <h1 class="db-title">Welcome back, {{ getFirstName(u.name) }}</h1>
      </div>
      <div class="db-header-actions">
        <button class="db-btn-solid" (click)="showCreateForm()">+ New League</button>
      </div>
    </div>

    @if (error()) {
      <div class="db-error">{{ error() }}</div>
    }

    <!-- Body -->
    <div class="db-body">

      <!-- LEFT -->
      <div class="db-left">

        @if (isLoading()) {
          <div class="db-loading">Loading leagues…</div>
        }

        <!-- Featured league panel -->
        @if (!isLoading() && featuredLeague(); as fl) {
          <div class="db-featured"
            [style.background-image]="'radial-gradient(ellipse at 0% 0%, oklch(96% 0.015 145 / 0.5) 0%, transparent 55%)'">

            <!-- League card (featured) -->
            <div class="league-card league-card--featured">
              <div class="lc-season">{{ fl.created_at ? 'EST. ' + (fl.created_at | date:'yyyy') : 'ACTIVE' }}</div>
              <div class="lc-badge" [style.background]="getLeagueStatus(fl).tone">{{ getLeagueStatus(fl).text }}</div>
              <div class="lc-art lc-art--featured">
                <div class="lc-team-icons">
                  @if (rosterLoading()) {
                    @for (i of [1,2,3,4,5,6]; track i) {
                      <div class="lc-team-avatar-skel"></div>
                    }
                  } @else if (rosterData(); as roster) {
                    @for (team of roster.teams; track team.id) {
                      <div class="lc-team-icon" [title]="team.name + (team.user_name ? ' · ' + team.user_name : '')">
                        @if (team.user_profile_picture) {
                          <img [src]="team.user_profile_picture" [alt]="team.user_name || team.name"
                               class="lc-team-avatar" referrerpolicy="no-referrer">
                        } @else {
                          <div class="lc-team-avatar-initials" [style.background]="getLeagueAccent($index)">{{ getInitials(team.user_name || team.name) }}</div>
                        }
                      </div>
                    }
                  }
                </div>
                <div class="lc-art-wash" [style.background]="'linear-gradient(135deg, ' + getLeagueAccent(0) + '18, transparent 50%)'"></div>
              </div>
              <div class="lc-footer lc-footer--featured">
                <a class="lc-name lc-name--featured" [routerLink]="['/d', fl.hash]">{{ fl.name }}</a>
                <div class="lc-stats lc-stats--featured">
                  <div class="lc-stat">
                    <div class="lc-stat-label">DRAFT</div>
                    <div class="lc-stat-value">{{ formatDraftDate(fl.draft_datetime) }}</div>
                  </div>
                  <div class="lc-stat">
                    <div class="lc-stat-label">TEAMS</div>
                    <div class="lc-stat-value">{{ fl.team_count ?? 0 }}/{{ fl.max_teams }}</div>
                  </div>
                  <div class="lc-stat">
                    <div class="lc-stat-label">MY TEAM</div>
                    <div class="lc-stat-value">{{ fl.team_name || '—' }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Info panel (top-right) -->
            <div class="db-featured-info">
              <div class="db-eyebrow">ON DECK</div>
              @if (draftCountdown()) {
                <div class="db-countdown">Live in <span class="db-countdown-accent">{{ draftCountdown() }}</span></div>
              } @else if (isDraftLive(fl)) {
                <div class="db-countdown">Draft is <span class="db-countdown-accent">live now</span></div>
              } @else {
                <div class="db-countdown">No draft time set</div>
              }
              <div class="db-featured-desc">
                {{ fl.is_commissioner ? "You're the commissioner." : "You're a member." }}
                {{ fl.team_count ?? 0 }}/{{ fl.max_teams }} teams confirmed,
                ${{ fl.team_budget }} budget, {{ fl.roster_size }} roster slots.
              </div>

              @if (fl.is_commissioner) {
                <div class="db-checklist">
                  <div class="db-eyebrow" style="margin-bottom: 8px">
                    COMMISSIONER CHECKLIST · {{ getChecklistCount(fl) }}/4
                  </div>
                  @for (item of getFeaturedChecklist(fl); track $index) {
                    <div class="db-check-row" [class.db-check-row--last]="$last">
                      <span class="db-check-box" [class.db-check-box--done]="item[1]">
                        @if (item[1]) { ✓ }
                      </span>
                      <span class="db-check-label" [class.db-check-label--done]="item[1]">{{ item[0] }}</span>
                    </div>
                  }
                </div>
              }

              @if (featuredLeague(); as fl) {
                <div class="db-join-field" style="margin-top: 16px">
                  <span class="db-join-label">JOIN</span>
                  <input class="db-join-input" [value]="getJoinUrl(fl)" readonly (click)="$any($event.target).select()">
                  <button class="db-join-copy" (click)="copyJoinLink(fl)">
                    {{ copiedLeagueId() === fl.id ? 'Copied!' : 'Copy' }}
                  </button>
                </div>
              }
            </div>

            <!-- Inline roster (spans full width below card + info) -->
            <div class="db-featured-roster">
              @if (rosterLoading()) {
                <div class="db-roster-skeleton">
                  <div class="db-skel-team-header">
                    <div class="db-skel-line db-skel-line--name"></div>
                    <div class="db-skel-line db-skel-line--budget"></div>
                  </div>
                  @for (r of [1,2,3,4]; track r) {
                    <div class="db-skel-row">
                      <div class="db-skel-cell db-skel-cell--lg"></div>
                      <div class="db-skel-cell db-skel-cell--sm"></div>
                      <div class="db-skel-cell db-skel-cell--sm"></div>
                      <div class="db-skel-cell db-skel-cell--xs"></div>
                    </div>
                  }
                </div>
              } @else if (rosterData(); as roster) {
                <!-- Charts -->
                <div class="db-featured-charts">
                  @if (statsLoading()) {
                    <div class="db-skel-chart"></div>
                    <div class="db-skel-chart"></div>
                    <div class="db-skel-chart"></div>
                  } @else if (statsData()) {
                    <section class="db-chart-section">
                      <div class="db-chart-label">COST BY POSITION</div>
                      <div class="db-chart-wrapper">
                        <canvas baseChart type="bar"
                          [data]="costBreakdownChartData()"
                          [options]="costBreakdownChartOptions">
                        </canvas>
                      </div>
                    </section>
                    <section class="db-chart-section">
                      <div class="db-chart-label">POSITION BREAKDOWN</div>
                      <div class="db-chart-wrapper">
                        <canvas baseChart type="bar"
                          [data]="positionChartData()"
                          [options]="positionChartOptions">
                        </canvas>
                      </div>
                    </section>
                    <section class="db-chart-section">
                      <div class="db-chart-label">DRAFT COST DISTRIBUTION</div>
                      @if (hasDraftData()) {
                        <div class="db-chart-wrapper">
                          <canvas baseChart type="bar"
                            [data]="costChartData()"
                            [options]="costChartOptions">
                          </canvas>
                        </div>
                      } @else {
                        <p class="db-roster-empty">No players drafted yet.</p>
                      }
                    </section>
                  }
                </div>

                @for (team of roster.teams; track team.id) {
                  <div class="db-roster-team">
                    <div class="db-roster-team-header">
                      <span class="db-roster-team-name">{{ team.name }}</span>
                      @if (team.user_name) {<span class="db-roster-owner">{{ team.user_name }}</span>}
                      <span class="db-roster-budget">${{ team.total_spent }} spent · ${{ team.remaining_budget }} left</span>
                    </div>
                    @if (team.roster.length === 0) {
                      <p class="db-roster-empty">No players drafted yet.</p>
                    } @else {
                      <table class="db-roster-table">
                        <thead>
                          <tr>
                            <th>Player</th>
                            <th>Pos</th>
                            <th>Team</th>
                            <th>$</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (p of team.roster; track p.player_id) {
                            <tr>
                              <td>{{ p.first_name }} {{ p.last_name }}</td>
                              <td>{{ p.position }}</td>
                              <td>{{ p.pro_team }}</td>
                              <td class="db-roster-cost">${{ p.cost }}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    }
                  </div>
                }
                @if (roster.teams.length === 0) {
                  <p class="db-roster-empty">No teams in this league yet.</p>
                }
              }
            </div>

          </div>
        }

        <!-- Mobile league actions (hidden on desktop, shown below featured on mobile) -->
        @if (!isLoading() && featuredLeague(); as fl) {
          <div class="db-mobile-league-actions">
            <app-league-actions [league]="fl" (edit)="showEditForm($event)" (delete)="deleteLeague($event)"></app-league-actions>
          </div>
        }

        @if (!isLoading() && leagues().length === 0) {
          <div class="db-empty">
            <p>No leagues yet.</p>
            <button class="db-btn-solid" (click)="showCreateForm()">+ Create your first league</button>
          </div>
        }

      </div>

      <!-- RIGHT sidebar -->
      <div class="db-sidebar">
        <div class="db-sidebar-card db-sidebar-league-actions">
          @if (featuredLeague(); as fl) {
            <div class="db-eyebrow" style="margin-bottom: 8px">{{ fl.name }}</div>
            <div class="db-quick-actions">
              <app-league-actions [league]="fl" (edit)="showEditForm($event)" (delete)="deleteLeague($event)"></app-league-actions>
            </div>
          }
        </div>

        @if (leagues().length > 0) {
          <div class="db-sidebar-card db-sidebar-card--grow">
            <div class="db-sidebar-header">
              <span class="db-eyebrow">YOUR LEAGUES</span>
              <span class="db-eyebrow" style="color: oklch(48% 0.12 145); cursor: pointer">{{ leagues().length }} TOTAL</span>
            </div>
            @for (league of leagues(); track league.id) {
              <div class="db-league-row" [class.db-league-row--selected]="featuredLeague()?.id === league.id">
                <div class="db-league-row-accent" [style.background]="getLeagueStatus(league).tone"></div>
                <div class="db-league-row-info" (click)="selectLeague(league)" style="cursor: pointer">
                  <div class="db-league-row-name">{{ league.name }}</div>
                  <div class="db-league-row-meta">{{ getLeagueStatus(league).text }} · {{ league.team_count ?? 0 }}/{{ league.max_teams }}</div>
                </div>
                <a class="db-league-row-action" [routerLink]="['/d', league.hash]" title="Open draft">
                  <fa-icon [icon]="faGavel"></fa-icon>
                </a>
                @if (league.is_commissioner) {
                  <button class="db-league-row-action db-league-row-action--danger" (click)="deleteLeague(league)" title="Delete league">
                    <fa-icon [icon]="faTrash"></fa-icon>
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>

    </div>

  }

  <!-- Modals -->
  <app-league-form-modal
    [isOpen]="showForm"
    [isEditing]="isEditing()"
    [editingAsCommissioner]="editingAsCommissioner()"
    [error]="error()"
    [league]="currentLeague"
    (saved)="saveLeague($event)"
    (closed)="cancelForm()"
  ></app-league-form-modal>

</div>
